// Module 19 (BL-380) G8 - Events Settings reads and writes. Every call runs under the signed-in user's own RLS: anyone with Events
// access can read the defaults (the forms need them); only a Tenant Admin can save (the database enforces both keys AND the admin).
import {
    CHECKIN_COLUMNS, CHECKIN_KEYS, DEFAULT_EVENT_SETTINGS, OPTIONAL_COLUMNS, OPTIONAL_KEYS, SETTINGS_COLUMNS, kitKeyFromLabel, settingsFromRow, settingsToRow,
} from './eventSettings';
import { DEFAULT_CREW_WORD, DEFAULT_EVENT_WORD } from './eventWords';

const KIT_COLUMNS = 'id, org_id, key, label, description, default_checklist, sort_order, is_active';

// Q-046 / G6: true when the database does not have the newer columns yet (code live minutes before migration 240 / 241 is applied).
export function missingWordColumns(error) {
    const text = `${error?.code || ''} ${error?.message || ''}`;
    return /42703|PGRST204/.test(text) && /event_word|crew_word|hotel_block_enabled|shuttle_enabled|checkin_enabled|checkin_qr_enabled/.test(text);
}
// G5: true when only the Check-In switches are missing (migration 246 not applied yet, 240/241 are).
const missingCheckinColumns = (error) => missingWordColumns(error) && /checkin_/.test(`${error?.message || ''}`);
const withoutKeys = (row, keys) => Object.fromEntries(Object.entries(row).filter(([key]) => !keys.includes(key)));

export async function loadEventSettings(client) {
    const first = await client.from('event_settings').select(`${SETTINGS_COLUMNS}, ${OPTIONAL_COLUMNS}, ${CHECKIN_COLUMNS}`).maybeSingle();
    if (!first.error) return settingsFromRow(first.data);
    if (!missingWordColumns(first.error)) throw first.error;
    if (missingCheckinColumns(first.error)) {
        const second = await client.from('event_settings').select(`${SETTINGS_COLUMNS}, ${OPTIONAL_COLUMNS}`).maybeSingle();
        if (!second.error) return settingsFromRow(second.data);
        if (!missingWordColumns(second.error)) throw second.error;
    }
    const { data, error } = await client.from('event_settings').select(SETTINGS_COLUMNS).maybeSingle();
    if (error) throw error;
    return settingsFromRow(data);
}

// The forms must never fail because settings could not be read: they fall back to the built-in defaults.
export async function loadEventSettingsOrDefaults(client) {
    try {
        return await loadEventSettings(client);
    } catch {
        return { ...DEFAULT_EVENT_SETTINGS };
    }
}

export async function saveEventSettings(client, orgId, userId, values) {
    const row = { org_id: orgId, ...settingsToRow(values), updated_by: userId || null };
    const first = await client.from('event_settings').upsert(row, { onConflict: 'org_id' }).select(`${SETTINGS_COLUMNS}, ${OPTIONAL_COLUMNS}, ${CHECKIN_COLUMNS}`).single();
    if (!first.error) return settingsFromRow(first.data);
    if (!missingWordColumns(first.error)) throw first.error;
    if (row.checkin_enabled === false || row.checkin_qr_enabled === false) {
        throw new Error('The Check-In switches cannot be saved yet. Please try again in a few minutes.');
    }
    if (missingCheckinColumns(first.error)) {
        const second = await client.from('event_settings').upsert(withoutKeys(row, CHECKIN_KEYS), { onConflict: 'org_id' }).select(`${SETTINGS_COLUMNS}, ${OPTIONAL_COLUMNS}`).single();
        if (!second.error) return settingsFromRow(second.data);
        if (!missingWordColumns(second.error)) throw second.error;
    }
    // The newer settings cannot be stored yet: other settings still save; a changed one says so plainly instead of being lost silently.
    if (row.event_word !== DEFAULT_EVENT_WORD || row.crew_word !== DEFAULT_CREW_WORD) {
        throw new Error('The words cannot be saved yet. Please try again in a few minutes.');
    }
    if (row.hotel_block_enabled === false || row.shuttle_enabled === false) {
        throw new Error('The Hotel & Travel switches cannot be saved yet. Please try again in a few minutes.');
    }
    const rest = withoutKeys(row, [...OPTIONAL_KEYS, ...CHECKIN_KEYS]);
    const { data, error } = await client.from('event_settings').upsert(rest, { onConflict: 'org_id' }).select(SETTINGS_COLUMNS).single();
    if (error) throw error;
    return settingsFromRow(data);
}

// Every type, on or off, in order (the board only offers the ones that are on).
export async function loadAllEventKits(client) {
    const { data, error } = await client.from('event_type_kits').select(KIT_COLUMNS).order('sort_order', { ascending: true }).order('label', { ascending: true });
    if (error) throw error;
    return data || [];
}

export async function updateEventKit(client, id, patch) {
    const { data, error } = await client.from('event_type_kits').update(patch).eq('id', id).select(KIT_COLUMNS).single();
    if (error) throw error;
    return data;
}

export async function addEventKit(client, orgId, { label, description, default_checklist }, existing = []) {
    const key = kitKeyFromLabel(label, existing.map((kit) => kit.key));
    const last = existing.reduce((max, kit) => Math.max(max, Number(kit.sort_order) || 0), 0);
    const { data, error } = await client
        .from('event_type_kits')
        .insert({ org_id: orgId, key, label: String(label).trim(), description: description ? String(description).trim() : null, default_checklist: default_checklist || [], sort_order: last + 10 })
        .select(KIT_COLUMNS)
        .single();
    if (error) throw error;
    return data;
}

// Moves a type one place up or down by swapping its order number with its neighbour's.
export async function moveEventKit(client, kits, id, direction) {
    const index = kits.findIndex((kit) => kit.id === id);
    const other = kits[index + (direction === 'up' ? -1 : 1)];
    if (index < 0 || !other) return kits;
    const a = kits[index];
    // equal order numbers (two types added at the same moment) are separated first, so the swap always changes the order
    const aOrder = Number(a.sort_order) || 0;
    const bOrder = Number(other.sort_order) || 0;
    const [nextA, nextOther] = aOrder === bOrder ? (direction === 'up' ? [bOrder - 1, bOrder] : [bOrder + 1, bOrder]) : [bOrder, aOrder];
    await updateEventKit(client, a.id, { sort_order: nextA });
    await updateEventKit(client, other.id, { sort_order: nextOther });
    return loadAllEventKits(client);
}

// "Restore starter kits": re-adds any of the four starter types that are missing; never touches one that exists (edited, turned off or moved).
export async function restoreStarterKits(client) {
    const { data, error } = await client.rpc('ensure_event_type_kits');
    if (error) throw error;
    return Number(data) || 0;
}
